import React, { useState } from 'react';
import {
  Avatar,
  Button,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Dismiss16Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  minimizedChat: {
    position: 'fixed',
    bottom: '0',
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 1100,
  },
  avatarWrapper: {
    position: 'relative',
    width: '56px',
    height: '56px',
  },
  closeButton: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    minWidth: '20px',
    minHeight: '20px',
    width: '20px',
    height: '20px',
    ...shorthands.padding('0'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('50%'),
    boxShadow: tokens.shadow8,
    zIndex: 1102,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  tooltip: {
    position: 'absolute',
    bottom: '70px',
    right: '0',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('12px'),
    minWidth: '200px',
    maxWidth: '280px',
    zIndex: 1101,
  },
  tooltipName: {
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  tooltipMessage: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

interface MinimizedChatProps {
  conversation: {
    id: number;
    name: string;
    avatar: string;
    preview?: string;
  };
  position: number;
  onClick: () => void;
  onClose: () => void;
}

const MinimizedChat: React.FC<MinimizedChatProps> = ({ conversation, position, onClick, onClose }) => {
  const styles = useStyles();
  const [showTooltip, setShowTooltip] = useState(false);
  const rightOffset = 16 + (position * 76); // 60px width + 16px gap

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <div
      className={styles.minimizedChat}
      style={{ right: `${rightOffset}px` }}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div className={styles.avatarWrapper} onClick={onClick}>
        <Avatar name={conversation.name} size={56} color="colorful" />
        <Button
          appearance="subtle"
          icon={<Dismiss16Regular />}
          className={styles.closeButton}
          onClick={handleClose}
        />
      </div>

      {showTooltip && (
        <div className={styles.tooltip}>
          <div className={styles.tooltipName}>{conversation.name}</div>
          <div className={styles.tooltipMessage}>
            {conversation.preview || 'No messages'}
          </div>
        </div>
      )}
    </div>
  );
};

export default MinimizedChat;
